import { useEffect, useMemo, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import useFeatureFlags from "@/hooks/useFeatureFlags";
import {
  useLazyGetGroupClassesQuery,
  useLazyGetRecommendedGroupClassQuery,
  useLazyGetRecommendedGroupsQuery,
} from "@/store/api/schedule/groupClasses";
import { useGetBasicInfoQuery, useGetFullProfileQuery } from "@/store/api/user";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import type {
  CandidatePatient,
  PatientRecommendation,
} from "@/utils/recommendedGroupClass/modalVisibility.util";
import {
  buildRecommendationsQueue,
  buildRecommendedGroupsQueue,
  isAllowedStatusCode,
  shouldSkipRecommendedGroupClassFetch,
} from "@/utils/recommendedGroupClass/modalVisibility.util";
import { getIsOnboardedOnMobile } from "@/utils/user.util";

interface RecommendedGroupClassModalVisibilityResult {
  isLoading: boolean;
  recommendations: PatientRecommendation[];
  shouldShowRecommendedGroupModal: boolean;
}

const toCandidatePatient = (
  patient: LinkedPatientProfile,
): CandidatePatient => ({
  patientExternalId: patient.externalId,
  universalStatusCode: patient.universalStatusCode,
});

const buildSupportCandidatePatients = (
  proxyPatients?: LinkedPatientProfile[],
): CandidatePatient[] => {
  if (isNil(proxyPatients)) return [];

  return proxyPatients
    .filter((patient) => isAllowedStatusCode(patient.universalStatusCode))
    .map(toCandidatePatient);
};

const buildPrimaryCandidatePatients = (
  fullProfileData?: FullProfileResponse,
): CandidatePatient[] => {
  if (isNil(fullProfileData)) return [];

  return [fullProfileData, ...(fullProfileData.linkedPatients ?? [])]
    .filter(
      (patient): patient is FullProfileResponse | LinkedPatientProfile =>
        !isNil(patient),
    )
    .filter((patient) => isAllowedStatusCode(patient.universalStatusCode))
    .map(toCandidatePatient);
};

const useRecommendedGroupClassModalVisibility =
  (): RecommendedGroupClassModalVisibilityResult => {
    const { isGroupClassRedesignEnabled } = useFeatureFlags();
    const { data: basicInfoData, isLoading: isBasicInfoLoading } =
      useGetBasicInfoQuery();
    const {
      data: fullProfileData,
      isLoading: isFullProfileLoading,
      isSuccess: isFullProfileSuccess,
    } = useGetFullProfileQuery();
    const { externalId: appUserId, isSupport } = useAppUserSelector();
    const proxyPatients = useAppUserProxyPatientsSelector();

    const [triggerRecommendedGroupClass] =
      useLazyGetRecommendedGroupClassQuery();
    const [triggerGroupClasses] = useLazyGetGroupClassesQuery();
    const [triggerRecommendedGroups] = useLazyGetRecommendedGroupsQuery();

    const [recommendations, setRecommendations] = useState<
      PatientRecommendation[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const isCancelledRef = useRef(false);
    const latestRequestIdRef = useRef(0);

    const isUserOnboarded = useMemo(() => {
      if (isNil(basicInfoData)) return false;
      return getIsOnboardedOnMobile(basicInfoData.onboardings);
    }, [basicInfoData]);

    const candidatePatients: CandidatePatient[] = useMemo(() => {
      if (!isFullProfileSuccess) return [];

      if (isSupport) {
        return buildSupportCandidatePatients(proxyPatients);
      }

      return buildPrimaryCandidatePatients(fullProfileData);
    }, [fullProfileData, isFullProfileSuccess, isSupport, proxyPatients]);

    useEffect(() => {
      const shouldSkipFetch = shouldSkipRecommendedGroupClassFetch({
        candidatePatients,
        isBasicInfoLoading,
        isFullProfileLoading,
        isFullProfileSuccess,
        isUserOnboarded,
      });

      if (shouldSkipFetch) {
        setRecommendations([]);
        setIsLoading(isBasicInfoLoading || isFullProfileLoading);
        return;
      }

      isCancelledRef.current = false;
      setIsLoading(true);
      latestRequestIdRef.current += 1;
      const requestId = latestRequestIdRef.current;

      const today = new Date();

      const updateRecommendations = async () => {
        const nextRecommendations = isGroupClassRedesignEnabled
          ? await buildRecommendedGroupsQueue({
              appUserId,
              candidatePatients,
              today,
              triggerRecommendedGroups,
            })
          : await buildRecommendationsQueue({
              appUserId,
              candidatePatients,
              today,
              triggerGroupClasses,
              triggerRecommendedGroupClass,
            });

        if (isCancelledRef.current) return;
        if (requestId !== latestRequestIdRef.current) return;

        setRecommendations(nextRecommendations);
        setIsLoading(false);
      };

      void updateRecommendations();

      return () => {
        isCancelledRef.current = true;
      };
    }, [
      appUserId,
      candidatePatients,
      isBasicInfoLoading,
      isFullProfileLoading,
      isFullProfileSuccess,
      isGroupClassRedesignEnabled,
      isUserOnboarded,
      triggerGroupClasses,
      triggerRecommendedGroupClass,
      triggerRecommendedGroups,
    ]);

    const shouldShowRecommendedGroupModal =
      !isLoading && !isEmpty(recommendations);

    return { isLoading, recommendations, shouldShowRecommendedGroupModal };
  };

export default useRecommendedGroupClassModalVisibility;
